import { motion } from 'framer-motion'
import { ChevronDown, Star, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const placeholderPillars = [
  { title: 'Know Yourself More Deeply', body: "Go beyond personality tests. Understand the actual parts of you — their fears, their stories, their gifts — and what happens when they're in conflict." },
  { title: 'Update Outdated Beliefs', body: "Most of what limits us isn't a lack of willpower. It's old stories running on autopilot. We find those stories and revise them." },
  { title: 'Relate Better — to Yourself and Others', body: "Self-compassion isn't soft. It's strategic. When you stop fighting your inner critic, you free up enormous energy." },
  { title: 'Live with More Intention', body: "Identify the values that actually drive you — not the ones you've been told to have — and align your choices with them." },
]

const placeholderQuotes = [
  '"The curious paradox is that when I accept myself just as I am, then I can change." — Carl Rogers',
  '"We cannot become what we need by remaining what we are." — Max De Pree',
  '"Your task is not to seek for love, but merely to seek and find all the barriers within yourself that you have built against it." — Rumi',
]

const benefits = [
  {
    title: 'A Clear Path to Self-Discovery',
    body: "In IFS therapy for self-development, you work with inner parts that hold you back, resist change, or feel misunderstood—without force or suppression.",
  },
  {
    title: 'Coaching Designed for Your Goals',
    body: 'Ideal for transitioning from one life phase to another, exploring deeper meaning, or wanting to show up more fully. Attuned to your needs—balance, new direction, or professional advancement.',
  },
  {
    title: 'Career Growth with Purpose',
    body: 'I work with professionals and executives on not just what the next step would be, but what the right step would be.',
  },
  {
    title: 'Trusted Support in California and Beyond',
    body: 'In person or online, I help you reconnect with your values, gain clarity, and build momentum. Wherever you begin, this gives you strength and purpose to carry on.',
  },
]

const steps = [
  {
    n: '1',
    title: 'Schedule Your Free Consultation',
    body: 'Choose a time that works best for you. This initial call is designed to help you understand your goals and challenges in a supportive, judgment-free environment.',
  },
  {
    n: '2',
    title: 'Share Your Story',
    body: "During our initial conversation, you'll have the opportunity to share what feels unclear, overwhelming, or stuck openly. This is your time to talk freely.",
  },
  {
    n: '3',
    title: 'Explore Your Options Together',
    body: "We'll explore how coaching can best support you, outline possible approaches, and determine if working together feels like the right fit.",
  },
  {
    n: '4',
    title: 'Create a Personalized Plan',
    body: "If we move forward, we'll design a coaching plan that aligns with your goals and priorities, while maintaining a sustainable and empowering pace.",
  },
  {
    n: '5',
    title: 'Begin Your Journey with Support',
    body: "With a clear plan in place, you'll step into sessions feeling supported. Each meeting builds momentum, helping you grow with confidence and ease.",
  },
]

const faqs = [
  {
    q: 'What is self-development coaching, and how does it work?',
    a: "It is a guided process of discovering one's values, aligning one's goals, and generating growth that feels authentic and sustainable.",
  },
  {
    q: 'Who benefits most from this kind of coaching?',
    a: 'People in transition, professionals, and leaders seeking career growth or a deeper alignment in life.',
  },
  {
    q: 'How soon can I expect results?',
    a: 'Some clients notice shifts from the very first sessions. Some require an extended period to bring in the deeper aspects of their growth with ongoing support.',
  },
]

const testimonials = [
  { quote: 'Douglas helped me see parts of myself I had ignored for years. I finally feel more connected and alive.', name: 'Michelle Ryan' },
  { quote: 'I came to Douglas looking for career guidance and left with clarity in all areas of life. Transformative experience.', name: 'Aaron Snyder' },
  { quote: "Every session was grounding and powerful. I've never felt more confident in my direction and decisions.", name: 'Chris Harrison' },
  { quote: 'The coaching process gave me tools to stop resisting growth. I feel balanced and finally moving forward with purpose.', name: 'Tanya George' },
]

function FAQ({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        {q}
        <ChevronDown size={16} className={`flex-shrink-0 ml-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{a}</p>}
    </div>
  )
}

export default function SelfDevelopmentCAPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">Services</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">Self Development Coaching California</motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">Finding Balance Beyond Success</motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-20">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-3xl">
          <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
            Who would understand this pull better than most professionals, great leaders, and individuals of high achievement? Mixed with that inward motion is the temperance or resistance that stops them from doing something.
          </p>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            It is a kind of inward tug-of-war that can drain even the staunchest person and leave them stuck or unsure about the next step to take. This is where IFS therapy for self-development comes in—different from any other approach. Rather than suppressing, ignoring, or working through parts that seem in conflict, this is a process that helps those parts learn to work together. It's about creating balance rather than forcing change.
          </p>
        </motion.div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Why Choose Self-Development Coaching?</motion.h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8">With more than 20 years of experience working with professionals, leaders worldwide, and individuals interested in growth beyond superficial accomplishments, I have had the opportunity to bring about meaningful changes in people's lives.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-6">
                <h3 className="font-bold text-emerald-700 dark:text-emerald-300 mb-2 text-sm">{b.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">5 Easy Steps to Talk to Me</motion.h2>
          <div className="space-y-4">
            {steps.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="flex gap-5 items-start">
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {s.n}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{s.title}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mt-0.5">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-6">FAQs</motion.h2>
          <div className="space-y-3 max-w-3xl">
            {faqs.map((f) => <FAQ key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">Why People Love to Talk!</motion.h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-6">
                <Star size={14} className="text-yellow-400 mb-3" />
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed italic mb-4">"{t.quote}"</p>
                <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold">— {t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Placeholder section — for Douglas to review ─────────────────── */}
        <div className="relative border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-3xl p-8">
          <div className="absolute -top-4 left-6">
            <span className="bg-amber-50 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-700">
              Draft Content — For Douglas to review &amp; accept
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-6 mt-2">The pillars and quotes below were drafted as placeholders. Douglas — please keep, modify, or remove as you see fit.</p>

          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-5">The Four Pillars We Work Through</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {placeholderPillars.map(({ title, body }) => (
              <div key={title} className="p-5 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
                <Sparkles size={14} className="text-blue-500 mb-2" />
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1.5">{title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {placeholderQuotes.map((q, i) => (
              <blockquote key={i} className="flex items-start gap-3 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
                <Star size={13} className="text-blue-400 flex-shrink-0 mt-1" />
                <p className="text-slate-600 dark:text-slate-300 text-sm italic leading-relaxed">{q}</p>
              </blockquote>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Ready to Begin?</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Growth that feels authentic and sustainable.</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">
            A free consultation is a judgment-free space to explore your goals and challenges. No pressure—just possibility.
          </p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book Free Consultation
          </CalendlyBtn>
        </motion.div>
      </div>
    </div>
  )
}
